import React, { useEffect, useMemo, useState } from 'react'
import T from 'prop-types'
import { Box, Stack, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import ConfirmationDialog from 'components/atoms/ConfirmationDialog'
import InputLabel from 'components/atoms/InputLabel'

import { getRepriceValues } from '../../../lib/repriceFee'

export default function RepriceDialog({
  car,
  repriceLocalValue,
  setRepriceLocalValue,
  openConfirmationDialog,
  handleCloseConfirmationDialog,
  handleConfirmConfirmationDialog,
  readOnly,
}) {
  const { t } = useTranslation()
  const [error, setError] = useState()

  const numberFormatter = new Intl.NumberFormat('it-IT')

  useEffect(() => {
    setRepriceLocalValue(car.reprice || '')
  }, [car.reprice, setRepriceLocalValue])

  const handleClose = () => {
    handleCloseConfirmationDialog()
  }

  const handleChange = (e) => {
    setRepriceLocalValue(e.target.value)
  }

  const carPrice = car?.price
  useEffect(() => {
    if (Number(repriceLocalValue) < carPrice) {
      setError(true)
    } else {
      setError()
    }
  }, [setError, repriceLocalValue, carPrice])

  const disabled = useMemo(
    () => !repriceLocalValue || Number(repriceLocalValue) <= 0,
    [repriceLocalValue]
  )
  const handleConfirm = () => {
    if (Number(repriceLocalValue) < car.price) {
      return
    }

    if (car.reprice === Number(repriceLocalValue)) {
      handleCloseConfirmationDialog()
      return
    }

    handleConfirmConfirmationDialog(car.vin, repriceLocalValue)
  }

  return (
    <ConfirmationDialog
      dataTestId="confirmation-dialog"
      title={t('carsToBuy.reprice.dialog.title')}
      content={
        <>
          <Typography>
            {`${t('carsToBuy.reprice.dialog.suggestedPrice')}: ${
              car.price && '€'
            }${
              car.price
                ? numberFormatter.format(car.price + (car.fee || 0))
                : '-'
            }`}
          </Typography>
          <Typography>
            {`${t('carsToBuy.reprice.dialog.fee')}: ${car.fee && '€'}${
              car.fee ? numberFormatter.format(car.fee) : '-'
            }`}
          </Typography>
          <Typography dataTestId="confirmation-dialog-price-1">
            {`${t('carsToBuy.reprice.dialog.sixtPrice')}: ${car.price && '€'}${
              car.price ? numberFormatter.format(car.price) : '-'
            }`}
          </Typography>
          <Box pb={2} />
          <Stack
            width="100%"
            flexDirection="row"
            justifyContent="space-vetween"
            alignItems="center"
          >
            <Box mr={3}>
              <InputLabel
                label={t('carsToBuy.reprice.dialog.repriceProposal')}
                inputId={`reprice.input`}
                sx={{ color: 'text.primary' }}
              />
              <TextField
                id={`reprice.input`}
                value={repriceLocalValue || ''}
                placeholder={t('carsToBuy.reprice.dialog.price')}
                type="number"
                size="small"
                variant="outlined"
                onChange={handleChange}
                disabled={readOnly}
              />
            </Box>
            <Box>
              <InputLabel
                label={t('carsToBuy.reprice.dialog.feeProposal')}
                inputId={`reprice.input`}
                sx={{ color: 'text.primary' }}
              />
              <TextField
                id={`repriceFee.input`}
                value={
                  repriceLocalValue
                    ? getRepriceValues(repriceLocalValue, car.feePercentage)
                        .repriceFee
                    : ''
                }
                type="number"
                size="small"
                variant="outlined"
                disabled
              />
            </Box>
          </Stack>
          <Typography variant="body2" color="textSecondary">
            {`${t('carsToBuy.reprice.dialog.message')}`}
          </Typography>
        </>
      }
      contentComponent={Box}
      open={openConfirmationDialog}
      readOnly={!!error || readOnly || disabled}
      handleClose={handleClose}
      handleSubmit={handleConfirm}
    />
  )
}

RepriceDialog.propTypes = {
  car: T.object.isRequired,
  repriceLocalValue: T.oneOfType([T.string, T.number]),
  setRepriceLocalValue: T.func,
  openConfirmationDialog: T.bool,
  handleCloseConfirmationDialog: T.func,
  handleConfirmConfirmationDialog: T.func,
  readOnly: T.bool,
}
